/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, forwardRef, useCallback, useEffect } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';

import { formatDate, getGestationAge, request } from '../services/utilities';
import { ivfAPI } from '../services/constants';
import { notifyError } from '../services/notify';

const packageColors = name => {
	const pkg = (name || '').toLowerCase();
	if (pkg.includes('chief')) return 'linear-gradient(135deg, #d4af37 0%, #b8960f 100%)';
	if (pkg.includes('standard')) return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
	if (pkg.includes('pay') || pkg.includes('go')) return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
	return 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)';
};

const ExtraBlock = ({ module, item: ivf, onSelectLmpDate, enrollmentId }) => {
	const [loaded, isLoaded] = useState(false);
	const [isset, setIsset] = useState(false);
	const [startDate, setStartDate] = useState(new Date());
	const [item, setItem] = useState(ivf);

	const init = useCallback(() => {
		const lmp = item && item.lmp ? new Date(moment(item.lmp)) : new Date();
		setStartDate(lmp);
	}, [item]);

	useEffect(() => {
		if (!isset) {
			init();
			setIsset(true);
		}
	}, [init, isset]);

	const getEnrollment = useCallback(async () => {
		try {
			const url = `${ivfAPI}/${enrollmentId}`;
			const rs = await request(url, 'GET', true);
			setItem(rs);
			isLoaded(true);
		} catch (error) {
			isLoaded(true);
			notifyError('could not get enrollment');
		}
	}, [enrollmentId]);

	useEffect(() => {
		if (!ivf && !loaded && enrollmentId) {
			getEnrollment();
		}
	}, [enrollmentId, getEnrollment, loaded, ivf]);

	if (!item) return null;

	let pregnancy_history = null;
	try {
		pregnancy_history = JSON.parse(item.pregnancy_history);
	} catch (e) {
		pregnancy_history = item.pregnancy_history;
	}

	const CustomInput = forwardRef(({ value, onClick }, ref) => (
		<a className="text-bold ml-2 text-primary" onClick={onClick} ref={ref} style={{ cursor: 'pointer' }}>
			<i className="os-icon os-icon-edit-32" />
		</a>
	));

	// Helper component to render beautiful dynamic statuses based on provided image rules
	const StatusIndicator = () => {
		const status = Number(item?.status);

		// 1. Success (Closed status 1 + success flag)
		if (status === 1 && (item?.is_success === true || item?.is_success === 'true')) {
			return <span className="badge badge-pill badge-success px-3 py-2 text-uppercase font-weight-bold shadow-sm">🎯 Success</span>;
		}
		// 2. Failed (Closed status 1 + failed flag)
		if (status === 1 && (item?.failed === true || item?.failed === 'true')) {
			return <span className="badge badge-pill badge-danger px-3 py-2 text-uppercase font-weight-bold shadow-sm">❌ Failed</span>;
		}
		// 3. Regular Closed (Status 1 without flags)
		if (status === 1) {
			return <span className="badge badge-pill badge-secondary px-3 py-2 text-uppercase font-weight-bold shadow-sm">🔒 Closed</span>;
		}
		// 4. discontinue
		if (status === 2) {
			return (
				<span className="badge badge-pill badge-warning text-dark px-3 py-2 text-uppercase font-weight-bold shadow-sm">⚠️ discontinue</span>
			);
		}
		// 5. Cancelled
		if (status === 3) {
			return <span className="badge badge-pill badge-dark px-3 py-2 text-uppercase font-weight-bold shadow-sm">🚫 Cancelled</span>;
		}
		// 6. Active / Open
		return (
			<span className="badge badge-pill badge-primary px-3 py-2 text-uppercase font-weight-bold shadow-sm animate-pulse">🟢 Active</span>
		);
	};

	return (
		<div className="card shadow-sm border-0 rounded mb-4 overflow-hidden">
			{/* Top Header Row for Status and Essential ID */}
			<div className="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3 border-bottom">
				<div className="d-flex align-items-center">
					<span className="text-uppercase font-weight-bold text-secondary mr-2 small">{module} Profile</span>
					{module === 'antenatal' && item?.serial_code && (
						<strong className="text-dark bg-white border rounded px-2 py-1 small">ANC #: {item.serial_code}</strong>
					)}
				</div>
				<div>
					<StatusIndicator />
				</div>
			</div>

			<div className="card-body p-3">
				{module === 'ivf' && (
					<div className="d-flex flex-wrap align-items-center gap-3 style-grid">
						{item.pregnancyTestDate && (
							<div className="info-pill">{`Pregnancy Test Date: ${moment(item.pregnancyTestDate).format('DD-MMM-YYYY')}`}</div>
						)}
						{item?.result && <div className="info-pill">{`Pregnancy Result: ${item?.result}`}</div>}
						{item?.indication && <div className="info-pill">{`Indication: ${item?.indication}`}</div>}
						{item?.treatmentPlan && <div className="info-pill">{`Treatment Plan: ${item?.treatmentPlan}`}</div>}
						{item?.prognosis && <div className="info-pill">{`Treatment Type: ${item?.prognosis}`}</div>}
						{item?.noOfEmbryoTransfer && <div className="info-pill">{`No. of Embryo Transfer: ${item?.noOfEmbryoTransfer}`}</div>}
						{item?.noOfEmbryoTransfer && <div className="info-pill">{`No Of Oocyte Retrieved: ${item?.noOfEmbryoTransfer}`}</div>}
						{item?.endometricThickness && <div className="info-pill">{`Endometric Thickness: ${item?.endometricThickness}`}</div>}
						{item.pregnancyTestDate && (
							<div className="info-pill">{`Treatment Date: ${moment(item.pregnancyTestDate).format('DD-MMM-YYYY')}`}</div>
						)}
						{item.embryoTransferDate && (
							<div className="info-pill">{`Transfer Date: ${moment(item.embryoTransferDate).format('DD-MMM-YYYY')}`}</div>
						)}
						{item.dateOfCommencement && (
							<div className="info-pill">{`Commencement Date: ${moment(item.dateOfCommencement).format('DD-MMM-YYYY')}`}</div>
						)}
						{item.dateOfStimulation && (
							<div className="info-pill">{`Stimulation Date: ${moment(item.dateOfStimulation).format('DD-MMM-YYYY')}`}</div>
						)}
						{item?.wifeLabDetails?.bloodGroup && <div className="info-pill">{`Wife Blood Group: ${item?.wifeLabDetails?.bloodGroup}`}</div>}
						{item?.wifeLabDetails?.chlamyda && <div className="info-pill">{`Wife Chlamyda: ${item?.wifeLabDetails?.chlamyda}`}</div>}
						{item?.wifeLabDetails?.genotype && <div className="info-pill">{`Wife Genotype: ${item?.wifeLabDetails?.genotype}`}</div>}
						{item?.wifeLabDetails?.hormonals?.lh && (
							<div className="info-pill">{`Wife Hormonals (LH): ${item?.wifeLabDetails?.hormonals?.lh}`}</div>
						)}
						{item?.wifeLabDetails?.hormonals?.amh && (
							<div className="info-pill">{`Wife Hormonals (AMH): ${item?.wifeLabDetails?.hormonals?.amh}`}</div>
						)}
						{item?.wifeLabDetails?.hormonals?.fsh && (
							<div className="info-pill">{`Wife Hormonals (FSH): ${item?.wifeLabDetails?.hormonals?.fsh}`}</div>
						)}
						{item?.wifeLabDetails?.hormonals?.prol && (
							<div className="info-pill">{`Wife Hormonals (PROL): ${item?.wifeLabDetails?.hormonals?.prol}`}</div>
						)}
						{item?.wifeLabDetails?.serology?.hiv && (
							<div className="info-pill">{`Wife Serology (HIV): ${item?.wifeLabDetails?.serology?.hiv}`}</div>
						)}
						{item?.wifeLabDetails?.serology?.hepb && (
							<div className="info-pill">{`Wife Serology (HEPB): ${item?.wifeLabDetails?.serology?.hepb}`}</div>
						)}
						{item?.wifeLabDetails?.serology?.hepc && (
							<div className="info-pill">{`Wife Serology (HEPC): ${item?.wifeLabDetails?.serology?.hepc}`}</div>
						)}
						{item?.wifeLabDetails?.serology?.vdrl && (
							<div className="info-pill">{`Wife Serology (VDRL): ${item?.wifeLabDetails?.serology?.vdrl}`}</div>
						)}
					</div>
				)}

				{module === 'antenatal' && (
					<>
						{/* General ANC Metrics Section */}
						<div className="d-flex flex-wrap align-items-center gap-3 border-bottom pb-3 mb-3 style-grid">
							<div className="info-pill font-weight-bold text-muted">Husband Profile</div>
							<div className="info-pill">{`Enrolled: ${formatDate(item.createdAt, 'DD MMM, YYYY')}`}</div>
							<div
								className="info-pill text-white font-weight-bold"
								style={{ background: packageColors(item.ancpackage?.name), border: 'none' }}
							>
								{`Package: ${item.ancpackage?.name || 'Nil'}`}
							</div>
							<div className="info-pill">{`Reason: ${item.booking_period}`}</div>
							<div className="info-pill d-flex align-items-center">
								<span>LMP: </span>
								<span className="text-underline font-weight-bold text-primary ml-1">{formatDate(item.lmp, 'MMM Do, YYYY')}</span>
								<DatePicker
									selected={startDate}
									onChange={date => {
										setStartDate(date);
										onSelectLmpDate(date);
									}}
									customInput={<CustomInput />}
									dateFormat="dd-MMM-yyyy"
									maxDate={new Date()}
								/>
							</div>
							<div className="info-pill">{`EDD: ${formatDate(item.edd, 'DD MMM, YYYY')}`}</div>
						</div>

						{/* Obstetric History Grid */}
						<div className="d-flex flex-wrap align-items-center gap-3 style-grid">
							<div className="info-pill bg-light border">{`Gravida: ${
								pregnancy_history?.gravida ? pregnancy_history?.gravida.replace(/[^0-9]/g, '') : '--'
							}`}</div>
							<div className="info-pill bg-light border">{`Para: ${pregnancy_history?.para || '--'}`}</div>
							<div className="info-pill bg-light border">{`Alive: ${pregnancy_history?.alive || '--'}`}</div>
							<div className="info-pill bg-light border">{`Miscarriages: ${pregnancy_history?.miscarriage || '--'}`}</div>
							<div className="info-pill bg-light border">{`Abortions: ${pregnancy_history?.abortion || '--'}`}</div>

							{Number(item.status) !== 1 && (
								<>
									<div className="info-pill border border-info text-info font-weight-bold">{`Gestation Age: ${getGestationAge(
										item.lmp
									)}`}</div>
									<div className="info-pill border border-warning text-warning font-weight-bold">
										{`No. of days to delivery: ${moment(item.edd).diff(moment(), 'days')} day(s)`}
									</div>
								</>
							)}
						</div>
					</>
				)}
			</div>

			{/* Inline Micro CSS Utilities to enhance styling layout */}
			<style jsx>{`
				.gap-3 {
					gap: 0.75rem;
				}
				.style-grid .info-pill {
					font-size: 0.875rem;
					color: #495057;
					background-color: #f8f9fa;
					padding: 0.4rem 0.75rem;
					border-radius: 6px;
					white-space: nowrap;
				}
				.text-underline {
					text-decoration: underline;
				}
				@keyframes pulse {
					0% {
						opacity: 0.8;
					}
					50% {
						opacity: 1;
					}
					100% {
						opacity: 0.8;
					}
				}
				.animate-pulse {
					animation: pulse 2s infinite ease-in-out;
				}
			`}</style>
		</div>
	);
};

export default ExtraBlock;
